/**
 * WMO weather code → emoji + i18n label.
 *
 * Standard WMO code 4677 (used by Open-Meteo). Codes are grouped: each
 * group shares the same icon + label key. Day vs night variants are
 * picked from `is_day` at render time.
 *
 * Reference: https://open-meteo.com/en/docs (Weather variable docs).
 */

export interface WmoEntry {
  /** Daytime emoji. */
  iconDay: string;
  /** Nighttime emoji. */
  iconNight: string;
  /** i18n key for short condition label. */
  labelKey: string;
}

const FALLBACK: WmoEntry = {
  iconDay: '\u{1F324}️',
  iconNight: '\u{1F319}',
  labelKey: 'weather_code_unknown',
};

const TABLE: Record<number, WmoEntry> = {
  0:  { iconDay: '☀️',          iconNight: '\u{1F319}',           labelKey: 'weather_code_clear' },
  1:  { iconDay: '\u{1F324}️',       iconNight: '\u{1F319}',           labelKey: 'weather_code_mostly_clear' },
  2:  { iconDay: '⛅',                iconNight: '☁️',        labelKey: 'weather_code_partly_cloudy' },
  3:  { iconDay: '☁️',          iconNight: '☁️',        labelKey: 'weather_code_overcast' },
  45: { iconDay: '\u{1F32B}️',       iconNight: '\u{1F32B}️',     labelKey: 'weather_code_fog' },
  48: { iconDay: '\u{1F32B}️',       iconNight: '\u{1F32B}️',     labelKey: 'weather_code_rime_fog' },
  51: { iconDay: '\u{1F327}️',       iconNight: '\u{1F327}️',     labelKey: 'weather_code_drizzle_light' },
  53: { iconDay: '\u{1F327}️',       iconNight: '\u{1F327}️',     labelKey: 'weather_code_drizzle' },
  55: { iconDay: '\u{1F327}️',       iconNight: '\u{1F327}️',     labelKey: 'weather_code_drizzle_dense' },
  56: { iconDay: '\u{1F328}️',       iconNight: '\u{1F328}️',     labelKey: 'weather_code_freezing_drizzle' },
  57: { iconDay: '\u{1F328}️',       iconNight: '\u{1F328}️',     labelKey: 'weather_code_freezing_drizzle_dense' },
  61: { iconDay: '\u{1F327}️',       iconNight: '\u{1F327}️',     labelKey: 'weather_code_rain_light' },
  63: { iconDay: '\u{1F327}️',       iconNight: '\u{1F327}️',     labelKey: 'weather_code_rain' },
  65: { iconDay: '\u{1F327}️',       iconNight: '\u{1F327}️',     labelKey: 'weather_code_rain_heavy' },
  66: { iconDay: '\u{1F328}️',       iconNight: '\u{1F328}️',     labelKey: 'weather_code_freezing_rain' },
  67: { iconDay: '\u{1F328}️',       iconNight: '\u{1F328}️',     labelKey: 'weather_code_freezing_rain_heavy' },
  71: { iconDay: '\u{1F328}️',       iconNight: '\u{1F328}️',     labelKey: 'weather_code_snow_light' },
  73: { iconDay: '\u{1F328}️',       iconNight: '\u{1F328}️',     labelKey: 'weather_code_snow' },
  75: { iconDay: '\u{1F328}️',       iconNight: '\u{1F328}️',     labelKey: 'weather_code_snow_heavy' },
  77: { iconDay: '\u{1F328}️',       iconNight: '\u{1F328}️',     labelKey: 'weather_code_snow_grains' },
  80: { iconDay: '\u{1F326}️',       iconNight: '\u{1F326}️',     labelKey: 'weather_code_showers_light' },
  81: { iconDay: '\u{1F326}️',       iconNight: '\u{1F326}️',     labelKey: 'weather_code_showers' },
  82: { iconDay: '⛈️',          iconNight: '⛈️',        labelKey: 'weather_code_showers_violent' },
  85: { iconDay: '\u{1F328}️',       iconNight: '\u{1F328}️',     labelKey: 'weather_code_snow_showers_light' },
  86: { iconDay: '\u{1F328}️',       iconNight: '\u{1F328}️',     labelKey: 'weather_code_snow_showers_heavy' },
  95: { iconDay: '⛈️',          iconNight: '⛈️',        labelKey: 'weather_code_thunderstorm' },
  96: { iconDay: '⛈️',          iconNight: '⛈️',        labelKey: 'weather_code_thunderstorm_hail' },
  99: { iconDay: '⛈️',          iconNight: '⛈️',        labelKey: 'weather_code_thunderstorm_hail_heavy' },
};

export function getWmoEntry(code: number): WmoEntry {
  return TABLE[code] ?? FALLBACK;
}

export function getWmoIcon(code: number, isDay = true): string {
  const e = getWmoEntry(code);
  return isDay ? e.iconDay : e.iconNight;
}

export function getWmoLabel(
  code: number,
  t: (key: string) => string,
): string {
  return t(getWmoEntry(code).labelKey);
}
